import React from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { CustomTooltip } from '../../keywordResearch/CustomTooltip';
import { openNewTab } from 'src/common/config/FunctionHelper';
import { TrackYourAIVisibilityCSS as css } from '../style';
import { orbitAiStepCSS } from '../OrbitAiStepCSS';

interface GBPLocationSelectorProps {
  /** Whether locations are currently being fetched. */
  loading: boolean;
  /** Array of GBP location objects. */
  locations: any[];
  /** The currently-selected value (business title or name). */
  selectedValue: string;
  /** Called when the user picks a location from the dropdown. */
  onSelect: (loc: any) => void;
  /** Tooltip text for the label. */
  tooltipText?: string;
  /** Optional actions rendered beside the dropdown (e.g. a settings button). */
  actions?: React.ReactNode;
  /** When true, the select dropdown is disabled. */
  disabled?: boolean;
  /** Optional function to determine if an individual location is disabled. */
  isOptionDisabled?: (loc: any) => boolean;
  /** Optional function to return a tooltip for disabled locations. */
  getOptionTooltip?: (loc: any) => string;
}

/**
 * Shared Google Business Profile location selector.
 * Used by both the Local SEO and Review Automation features.
 */
export const GBPLocationSelector: React.FC<GBPLocationSelectorProps> = ({
  loading,
  locations,
  selectedValue,
  onSelect,
  tooltipText = 'Select the Google Business Profile location you want to optimize.',
  actions,
  disabled = false,
  isOptionDisabled,
  getOptionTooltip
}) => {
  const firstAvailableLoc = locations.find((l: any) => !isOptionDisabled || !isOptionDisabled(l)) || locations[0];
  const selectValue = selectedValue || firstAvailableLoc?.title || firstAvailableLoc?.name || '';

  const selectDropdown = (
    <FormControl fullWidth size="small">
      <Select
        disabled={disabled}
        value={selectValue}
        onChange={(e) => {
          const chosenTitle = e.target.value;
          const chosenLoc = locations.find(
            (loc: any) => (loc?.title || loc?.name) === chosenTitle
          );
          if (chosenLoc && (!isOptionDisabled || !isOptionDisabled(chosenLoc))) {
            onSelect(chosenLoc);
          }
        }}
        displayEmpty
        sx={{
          borderRadius: '6px',
          backgroundColor: 'var(--color-white)',
          fontSize: '14px',
          fontFamily: 'Inter',
          color: 'var(--color-text-heading)',
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            py: '8px'
          }
        }}
        MenuProps={{
          disableScrollLock: true
        }}
      >
        {locations.map((loc: any, idx: number) => {
          const titleStr = loc?.title || loc?.name;
          const isLocDisabled = isOptionDisabled ? isOptionDisabled(loc) : false;
          const disabledTooltip = getOptionTooltip ? getOptionTooltip(loc) : '';

          const itemContent = (
            <MenuItem key={loc?.name || idx} value={titleStr} disabled={isLocDisabled}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  component="img"
                  src="/static/images/icons/local-seo/google-home.svg"
                  alt="GBP"
                  sx={{ width: 18, height: 18 }}
                />
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {titleStr}
                </Typography>
              </Box>
            </MenuItem>
          );

          if (isLocDisabled && disabledTooltip) {
            return (
              <CustomTooltip
                key={loc?.name || idx}
                title={
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#120242', fontFamily: 'Inter' }}>
                    {disabledTooltip}
                  </Typography>
                }
                placement="right"
                arrow
              >
                <Box component="span" sx={{ display: 'block', width: '100%' }}>
                  {itemContent}
                </Box>
              </CustomTooltip>
            );
          }

          return itemContent;
        })}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={orbitAiStepCSS?.featureRow}>
      <Box sx={orbitAiStepCSS?.featureBox}>
        <Box sx={css.labelRow}>
          <Typography sx={css.label}>
            Select Google Business Profile
          </Typography>
          <CustomTooltip
            title={
              <Box>
                <Typography sx={orbitAiStepCSS?.featureDesc}>
                  {tooltipText}
                </Typography>
              </Box>
            }
            placement="bottom"
            arrow
          >
            <HelpIcon sx={css.helpIcon} />
          </CustomTooltip>
        </Box>
        <Box sx={{ mt: '8px' }}>
          {loading ? (
            <Box display="flex" alignItems="center" gap={1.5} py={1}>
              <CircularProgress size={20} />
              <Typography sx={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                Loading Google Business Profiles...
              </Typography>
            </Box>
          ) : locations.length > 0 ? (
            actions ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexDirection: { xs: 'column', sm: 'row' }
                }}
              >
                <Box sx={{ flex: 1, width: '100%' }}>
                  {selectDropdown}
                </Box>
                {actions}
              </Box>
            ) : (
              selectDropdown
            )
          ) : (
            <Box
              sx={{
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: 'var(--color-bg-muted-alt)',
                border: '1px dashed var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography sx={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                No connected Google Business Profile found.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => openNewTab('/local-seo')}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  borderColor: 'var(--color-primary)',
                  color: 'var(--color-primary)',
                  maxHeight: '40px'
                }}
              >
                Connect Profile
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
